import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Building2, Loader2, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CompanyCard } from "@/components/companies/CompanyCard";
import { useCompanies } from "@/lib/api/companies.queries";
import { useActiveTeam, useCanWriteCompanies } from "@/lib/team-context";
import type { ListStatusFilter } from "@/lib/api/companies";

export const Route = createFileRoute("/companies/")({
  component: CompaniesPage,
});

function CompaniesPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CompaniesContent />
      </AppShell>
    </RequireAuth>
  );
}

function CompaniesContent() {
  const { t } = useTranslation();
  const { activeTeam, ready: teamReady } = useActiveTeam();
  const canCreate = useCanWriteCompanies();
  const [statusFilter, setStatusFilter] = useState<ListStatusFilter>("active");
  const [searchTerm, setSearchTerm] = useState("");
  const companiesQuery = useCompanies({ status: statusFilter });

  const filtered = useMemo(() => {
    const all = companiesQuery.data?.companies ?? [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (c) =>
        c.legalName.toLowerCase().includes(q) ||
        c.tradeName?.toLowerCase().includes(q) ||
        c.nit.includes(q),
    );
  }, [companiesQuery.data, searchTerm]);

  if (teamReady && !activeTeam) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <h2 className="text-lg font-semibold">
              {t("companies.no_team.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("companies.no_team.body")}
            </p>
            <Button asChild>
              <Link to="/teams">{t("companies.no_team.cta")}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("companies.page.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("companies.page.subtitle")}
          </p>
        </div>
        {canCreate ? (
          <Button asChild>
            <Link to="/companies/new">
              <Plus className="h-4 w-4" />
              {t("companies.page.create_cta")}
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("companies.page.search_placeholder")}
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as ListStatusFilter)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">
              {t("companies.page.filter.active")}
            </SelectItem>
            <SelectItem value="archived">
              {t("companies.page.filter.archived")}
            </SelectItem>
            <SelectItem value="all">
              {t("companies.page.filter.all")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {companiesQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : companiesQuery.isError ? (
        <ErrorState
          title={t("companies.page.error.title")}
          description={t("companies.page.error.body")}
          onRetry={() => void companiesQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : filtered.length === 0 ? (
        <EmptyState hasSearch={searchTerm.length > 0} canCreate={canCreate} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((company) => (
            <CompanyCard key={company.id} company={company} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ hasSearch, canCreate }: { hasSearch: boolean; canCreate: boolean }) {
  const { t } = useTranslation();
  if (hasSearch) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <h2 className="text-lg font-semibold">
            {t("companies.empty.search_title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("companies.empty.search_body")}
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">
            {t(canCreate ? "companies.empty.title" : "companies.read_only.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(canCreate ? "companies.empty.body" : "companies.read_only.body")}
          </p>
        </div>
        {canCreate ? (
          <Button asChild>
            <Link to="/companies/new">
              <Plus className="h-4 w-4" />
              {t("companies.empty.cta")}
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
